import type { AxiosAdapter } from 'axios'
import { CapacitorHttp } from '@capacitor/core'
import { isNativeApp } from '../device'

function buildUrl(url?: string, baseURL?: string): string {
  if (!url) {
    return baseURL || ''
  }

  if (/^https?:\/\//i.test(url)) {
    return url
  }

  if (!baseURL) {
    return url
  }

  return `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`
}

function normalizeHeaders(headers: any): Record<string, string> {
  if (!headers) {
    return {}
  }

  const rawHeaders = typeof headers.toJSON === 'function' ? headers.toJSON() : headers

  return Object.entries(rawHeaders).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc
    }

    acc[key] = Array.isArray(value) ? value.join(', ') : String(value)
    return acc
  }, {})
}

function normalizeParams(params: any): Record<string, string | string[]> | undefined {
  if (!params) {
    return undefined
  }

  return Object.entries(params).reduce<Record<string, string | string[]>>((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc
    }

    if (Array.isArray(value)) {
      acc[key] = value.map(String)
      return acc
    }

    acc[key] = String(value)
    return acc
  }, {})
}

function normalizeRequestData(data: any) {
  if (typeof data !== 'string') {
    return data
  }

  try {
    return JSON.parse(data)
  } catch {
    return data
  }
}

function normalizeResponseData(data: any, headers: Record<string, string>) {
  if (typeof data !== 'string') {
    return data
  }

  const contentType = (headers['content-type'] || headers['Content-Type'] || '').toLowerCase()
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(data)
    } catch {
      return data
    }
  }

  return data
}

/**
 * Adapter Axios que usa HTTP nativo (quando disponível)
 *
 * Vantagens do HTTP nativo (quando disponível):
 * - Bypass de CORS/preflight do WebView
 * - Timeouts mais previsíveis em redes móveis
 * - Menos falhas intermitentes em iOS/Android
 */
export function getCapacitorAdapter(): AxiosAdapter | undefined {
  if (!isNativeApp()) {
    return undefined
  }

  return async (config: any) => {
    const headers = normalizeHeaders(config.headers)
    const response = await CapacitorHttp.request({
      url: buildUrl(config.url, config.baseURL),
      method: (config.method || 'GET').toUpperCase(),
      headers,
      params: normalizeParams(config.params),
      data: normalizeRequestData(config.data),
      connectTimeout: config.timeout || 30000,
      readTimeout: config.timeout || 30000,
      responseType: 'json',
      shouldEncodeUrlParams: true,
    })

    return {
      data: normalizeResponseData(response.data, response.headers || {}),
      status: response.status,
      statusText: String(response.status || ''),
      headers: response.headers || {},
      config,
      request: null,
    }
  }
}

/**
 * Verifica se o adapter deve ser usado
 */
export function shouldUseNativeAdapter(): boolean {
  return isNativeApp()
}

/**
 * Obtém informações sobre o adapter em uso
 */
export function getAdapterInfo() {
  const native = isNativeApp()
  return {
    isNative: native,
    adapter: native ? 'CapacitorHttp' : 'XMLHttpRequest (web)',
    platform: native ? (window as any).Capacitor?.getPlatform() : 'web',
    note: native
      ? 'Usando transporte HTTP nativo do Capacitor para evitar CORS do WebView'
      : 'Usando XMLHttpRequest no navegador',
  }
}
